"use client";

import React from "react";
import { CircleQuestionMark as HelpCircle, PhoneCall, FileText, ArrowUpRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
interface FaqItem {
  id: string;
  index: string;
  question: string;
  answer: string;
}
const FAQ_ITEMS: FaqItem[] = [{
  id: "item-1",
  index: "01",
  question: "What should I look for when buying a mechanical bull?",
  answer: "Start with intended use, condition, controller, included equipment, power and space requirements, parts availability, price and shipping. Then compare those details for the exact machines you are considering."
}, {
  id: "item-2",
  index: "02",
  question: "Should I buy a new or used mechanical bull?",
  answer: "Buy new when a known starting condition and current product information matter most. Consider used when reducing the initial purchase cost is important and enough condition information is available to evaluate the exact machine."
}, {
  id: "item-3",
  index: "03",
  question: "Where can I buy a mechanical bull?",
  answer: "Mechanical bulls are available through specialist equipment sellers, manufacturers, distributors and used-equipment sellers. Compare the exact machine, package contents, condition and parts availability before purchasing."
}, {
  id: "item-4",
  index: "04",
  question: "What should come with a mechanical bull?",
  answer: "It depends on the listing. A complete package may include the bull, motion base, controller, inflatable safety arena, blower and accessories. Always confirm what is included."
}, {
  id: "item-5",
  index: "05",
  question: "How much space does a mechanical bull need?",
  answer: "Space requirements vary by machine and safety arena. Use the dimensions for the exact equipment package rather than applying one generic footprint to every mechanical bull."
}, {
  id: "item-6",
  index: "06",
  question: "What power does a mechanical bull need?",
  answer: "Electrical requirements vary between models. Check the product-specific requirements for the bull, controller and blower."
}, {
  id: "item-7",
  index: "07",
  question: "Are replacement parts important when choosing a bull?",
  answer: "Yes. Access to compatible controllers, switches, inflatable components, blowers and other replacement parts can affect long-term ownership and downtime."
}, {
  id: "item-8",
  index: "08",
  question: "How much does a mechanical bull cost?",
  answer: "Cost varies according to the machine, condition, included equipment, accessories and delivery requirements. Compare current inventory and complete packages rather than relying on one generic industry price."
}];
export default function BuyingFaq() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="mechanicalbullbuyingfaq" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="max-w-[80rem] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.35,
        ease: "easeOut"
      }} className="flex flex-col md:flex-row md:items-end justify-between border-b border-border pb-8 mb-10 gap-6">
          <div className="space-y-3 max-w-3xl">
            <div className="flex items-center gap-2 text-xs font-mono tracking-widest text-primary uppercase">
              <span className="inline-block size-2 bg-primary"></span>
              <span>Commercial Purchasing Reference</span>
              <span className="text-muted-foreground">/</span>
              <span className="text-muted-foreground">08 Primary Inquiries</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight uppercase text-foreground leading-none">
              Mechanical Bull Buying FAQ
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground font-sans max-w-2xl leading-relaxed">
              Essential technical specifications, operational logistics, and procurement standards to evaluate before purchasing commercial motion equipment.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <Button asChild variant="outline" size="sm" className="font-mono text-xs uppercase tracking-wider rounded-none border-border hover:border-primary">
              <Link to="#">
                <FileText className="size-3.5 mr-1.5" />
                Full Buyer Guide
              </Link>
            </Button>
            <Button asChild size="sm" className="font-mono text-xs uppercase tracking-wider rounded-none bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactPage">
                <PhoneCall className="size-3.5 mr-1.5" />
                Speak to Specialist
              </Link>
            </Button>
          </div>
        </motion.div>

        {/* Industrial FAQ Card Container */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        ease: "easeOut",
        delay: 0.1
      }} className="border border-border bg-card shadow-xs">
          {/* Machine Header Strip */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/40 font-mono text-xs text-muted-foreground uppercase tracking-wider">
            <span className="flex items-center gap-2">
              <HelpCircle className="size-3.5 text-primary" />
              <span>Technical Verification & Procurement Criteria</span>
            </span>
            <span className="hidden sm:inline-block">Commercial Standard v4.2</span>
          </div>

          {/* Accordion Component */}
          <Accordion type="single" collapsible className="w-full">
            {FAQ_ITEMS.map((item, idx) => <AccordionItem key={item.id} value={item.id} data-index={idx} className="border-b border-border last:border-b-0 px-4 sm:px-6 transition-colors duration-150 hover:bg-muted/15">
                <AccordionTrigger className="py-5 sm:py-6 text-left hover:no-underline group">
                  <div className="flex items-start sm:items-center gap-4 sm:gap-6 pr-4">
                    <span className="font-mono text-xs sm:text-sm font-semibold text-primary/90 bg-muted px-2 py-1 rounded-none shrink-0 tracking-wider">
                      {item.index}
                    </span>
                    <span className="font-serif text-lg sm:text-xl md:text-2xl font-semibold uppercase tracking-tight text-card-foreground group-hover:text-primary transition-colors duration-150">
                      {item.question}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-1 pb-6 pl-10 sm:pl-16 pr-4 sm:pr-8 text-muted-foreground font-sans text-sm sm:text-base leading-relaxed">
                  <div className="border-l-2 border-primary/40 pl-4 py-1">
                    <p>{item.answer}</p>
                  </div>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        {/* Industrial Footer Note */}
        <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 border border-dashed border-border bg-background text-xs font-mono text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="size-1.5 bg-primary rounded-full"></span>
            <span>Need model-specific electrical drawings or arena layout dimensions?</span>
          </div>
          <Link to="#" className="inline-flex items-center gap-1 text-foreground font-medium hover:text-primary transition-colors underline-offset-4 hover:underline">
            <span>Review setup & electrical specs</span>
            <ArrowUpRight className="size-3" />
          </Link>
        </div>
      </div>
    </section>;
}